// @ts-nocheck
/* eslint-disable */
import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { 
    Trash2, 
    Plus, 
    ExternalLink, 
    ChevronDown,
    AlertCircle 
} from 'lucide-react';
import useAppStore from '../store/appStore';
import { useCalculationsSelector } from '../store/selectors/calculations';
import { 
    ExtensionsKalkulatorExtension, 
    ExtensionsKalkulatorInputs,
    EmployeeData,
    Geraet
} from '../store/types';
import PreislistenManager from '../components/PreislistenManager'; 

// --- INTERFACES FÜR UI ---
interface EditableFieldProps {
    value: string | number;
    onSave: (val: any) => void;
    isNumeric?: boolean;
    unit?: string;
    formatOptions?: Intl.NumberFormatOptions;
    className?: string;
    align?: 'left' | 'center' | 'right';
    tooltip?: string;
    formatCurrency?: any;
    type?: string;
    readOnly?: boolean;
    placeholder?: string;
}

interface AuswertungResultProps {
    label: string;
    value: string;
    subtext?: string;
    valueClassName?: string;
    variant?: 'default' | 'highlight';
    additionalInfo?: string;
    tooltip?: string;
}

// --- HILFSFUNKTIONEN ---
const formatNumberDE = (value: number, options: Intl.NumberFormatOptions = {}) => {
    return new Intl.NumberFormat('de-DE', options).format(value || 0);
};

const formatCurrencyValue = (value: number, digits = 2) => {
    return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(value || 0);
};

const formatCurrencyStrict = (value: number, currency: string = 'EUR', digits = 2) => {
    const num = new Intl.NumberFormat('de-DE', { 
        minimumFractionDigits: digits, 
        maximumFractionDigits: digits 
    }).format(value || 0);
    return `${num} ${currency}`;
};

const parseNumberDE = (str: any): number => {
    if (typeof str === 'number') return str;
    if (str === null || str === undefined || str === '') return 0;
    return parseFloat(String(str).replace(/\./g, '').replace(',', '.'));
};

const splitValueAndUnit = (str: string) => {
    if(typeof str !== 'string') return { val: str, unit: '' };
    const lastSpaceIndex = str.lastIndexOf(' ');
    if (lastSpaceIndex !== -1) {
        return { 
            val: str.substring(0, lastSpaceIndex), 
            unit: str.substring(lastSpaceIndex + 1) 
        };
    }
    if (str.endsWith('%')) {
        return { val: str.slice(0, -1), unit: '%' };
    }
    return { val: str, unit: '' };
};

// --- UNTERKOMPONENTEN ---
const CustomStyles = () => (
    <style>{`
      :root { 
          --custom-beige: #E6C299;
          --slate-800: #1e293b;
          --slate-900: #0f172a;
          --slate-700: #334155;
      }
      .text-customBeige { color: var(--custom-beige); text-shadow: 0 0 2px rgba(0,0,0,0.5); }
      .bg-customBeige { background-color: var(--custom-beige); }
      .border-customBeige { border-color: var(--custom-beige); }
      
      body { 
          font-family: 'Inter', sans-serif;
          color: #f9fafb; 
          background-color: #030303; 
      }
      
      .font-numeric {
          font-family: 'JetBrains Mono', monospace;
          font-variant-numeric: tabular-nums;
      }

      .editable-field { 
          background: linear-gradient(145deg, #000000, #0a0a0a);
          color: white; 
          border: 1px solid #222;
          border-radius: 0.5rem; 
          padding: 0.5rem; 
          text-align: center; 
          cursor: text; 
          min-width: 60px; 
          font-family: 'JetBrains Mono', monospace;
          transition: all 0.2s;
          box-shadow: inset 2px 2px 5px rgba(0,0,0,0.8), inset -1px -1px 2px rgba(255,255,255,0.05);
      }
      .editable-field:focus { 
          outline: none;
          border-color: var(--custom-beige);
          box-shadow: 0 0 10px rgba(230, 194, 153, 0.2); 
      }
      .editable-field[data-placeholder]:empty::before { content: attr(data-placeholder); color: #475569; pointer-events: none; }
      
      .calculated-field {
          background: rgba(0,0,0,0.4);
          color: var(--custom-beige);
          border-radius: 0.5rem;
          text-align: center;
          width: 100%;
          font-family: 'JetBrains Mono', monospace; 
          padding: 0.5rem;
          font-weight: 600;
          border: 1px solid #1a1a1a;
          box-shadow: inset 0 2px 10px rgba(0,0,0,0.5);
      }

      input[type="checkbox"] { 
          -webkit-appearance: none; 
          appearance: none; 
          background-color: #000000; 
          border: 1px solid #333; 
          border-radius: 0.25rem; 
          width: 1.25rem; 
          height: 1.25rem; 
          cursor: pointer; 
          position: relative; 
          vertical-align: middle; 
          box-shadow: inset 0 0 5px rgba(0,0,0,1); 
      }
      input[type="checkbox"]:checked { 
          background-color: var(--custom-beige); 
          border-color: var(--custom-beige); 
          box-shadow: 0 0 10px var(--custom-beige); 
      }
      input[type="checkbox"]:checked::after { 
          content: '✔'; 
          position: absolute; 
          color: #000; 
          font-size: 0.8rem; 
          font-weight: bold; 
          top: 50%; 
          left: 50%; 
          transform: translate(-50%, -50%); 
      }

      .action-btn { 
          background: linear-gradient(145deg, #111, #000);
          color: #E6C299; 
          padding: 0.25rem 0.75rem; 
          border-radius: 0.375rem; 
          border: 1px solid #222; 
          transition: all 0.2s; 
          white-space: nowrap;
          box-shadow: 2px 2px 5px rgba(0,0,0,0.5), -1px -1px 2px rgba(255,255,255,0.05);
      }
      .action-btn:hover { 
          background: #E6C299; 
          color: #000;
          transform: translateY(-1px); 
          box-shadow: 0 0 10px rgba(230,194,153,0.3); 
      }
      .action-btn:active { 
          transform: translateY(1px); 
          box-shadow: inset 2px 2px 5px rgba(0,0,0,0.5); 
      }
      
      .remove-btn { 
          background-color: #111; 
          color: #64748b; 
          border-radius: 0.375rem; 
          width: 1.75rem; 
          height: 1.75rem; 
          border: 1px solid #222; 
          cursor: pointer; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          transition: all 0.2s; 
      }
      .remove-btn:hover { 
          background-color: #ef4444; 
          color: white;
          border-color: #ef4444;
          box-shadow: 0 0 8px #ef4444; 
      }

      .custom-select { 
          background-color: #000000; 
          color: var(--custom-beige); 
          border-radius: 0.5rem; 
          padding: 0.5rem; 
          border: 1px solid #222; 
          cursor: pointer; 
          box-shadow: inset 0 0 5px rgba(0,0,0,1); 
      }

      /* Carbon Weave Design */
      .carbon-weave-bg {
          background-color: #111;
          background-image:
              radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
              radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
              radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
              radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
              radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
          background-size: 20px 20px;
      }

      .carbon-panel {
          position: relative;
          border-radius: 1.5rem;
          border: 1px solid #1a1a1a;
          box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.02);
          overflow: hidden;
          z-index: 1;
      }

      .carbon-panel.extensions-kalkulator-panel {
          overflow: visible;
      }

      .carbon-panel.extensions-kalkulator-panel .carbon-content {
          overflow: visible;
      }

      .extensions-kalkulator-config {
          overflow: visible;
      }

      .extensions-kalkulator-config .extensions-kalkulator-table-scroll {
          overflow-x: auto;
          overflow-y: visible;
      }

      .carbon-panel::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.55));
          pointer-events: none;
          z-index: 1;
      }

      .carbon-content {
          position: relative;
          z-index: 10;
      }

      .gauge-container-3d {
          background: radial-gradient(circle at center, #111 0%, #030303 100%);
          box-shadow: inset 0 0 40px rgba(0,0,0,0.95), 0 10px 25px rgba(0,0,0,0.6);
          border: 1px solid #1a1a1a;
          border-radius: 1.5rem;
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
      }

      .text-verlust { color: #ef4444 !important; text-shadow: 0 0 15px rgba(239, 68, 68, 0.6) !important; }
      .text-kostendeckend { color: #facc15; text-shadow: 0 0 15px rgba(250, 204, 21, 0.6) !important; }
      .text-gewinn { color: #22c55e; text-shadow: 0 0 15px rgba(34, 197, 94, 0.6) !important; }
      .text-gut { color: #3b82f6; text-shadow: 0 0 15px rgba(59, 130, 246, 0.6) !important; }
      .text-exzellent { color: #8b5cf6; text-shadow: 0 0 15px rgba(139, 92, 246, 0.6) !important; }
      
      circle#gewinn-gauge-progress { transition: stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.5s ease-in-out; }
      .compact-table th, .compact-table td { padding: 0.5rem 0.75rem; font-size: 0.85rem; border-bottom: 1px solid #1a1a1a; vertical-align: middle;}
      .compact-table th { background-color: rgba(0,0,0,0.5); color: var(--custom-beige); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #222; }

      .modal-input {
          width: 100%;
          background: rgba(0,0,0,0.8);
          border: 1px solid #222;
          color: white;
          padding: 0.75rem;
          border-radius: 0.5rem;
          margin-top: 0.5rem;
      }
      .modal-input:focus { outline: none; border-color: var(--custom-beige); }

      .toggle-container { background-color: #000; border: 1px solid #333; border-radius: 9999px; display: inline-flex; flex-direction: row; padding: 4px; align-items: center; cursor: pointer; user-select: none; }
      .toggle-pill { padding: 6px 16px; font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; border-radius: 9999px; cursor: pointer; transition: all 0.2s ease; white-space: nowrap; }
      .toggle-pill-active { background-color: #E6C299; color: #000; box-shadow: 0 2px 10px rgba(230,194,153,0.3); }
      .toggle-pill-inactive { color: #64748b; }
      .toggle-pill-inactive:hover { color: white; }
      .toggle-arrow { color: #E6C299; font-size: 0.8rem; padding: 0 8px; opacity: 0.5; pointer-events: none; }

      .extensions-margin-calc-result {
          white-space: nowrap;
      }

      /* Tooltip system */
      [data-tooltip-id] { position: relative; }
      .pe-tooltip { background-color: var(--custom-beige); color: black; border: 1px solid white; font-weight: 700; border-radius: 0.25rem; padding: 0.5rem; position: fixed; z-index: 2147483647; display: none; font-size: 0.8rem; max-width: 250px; box-shadow: 0 10px 20px rgba(0,0,0,0.6); pointer-events: none; }
      .pe-tooltip.visible { display: block; }

      /* ===== LIGHT-MODE OVERRIDES (Warm-Platin) — Dark bleibt via html.dark unangetastet ===== */
      html:not(.dark) body {
          color: var(--pe-text-primary);
          background-color: var(--pe-bg-primary);
      }
      html:not(.dark) .text-customBeige { color: var(--pe-accent-text); text-shadow: none; }
      html:not(.dark) .editable-field {
          background: var(--pe-bg-input);
          color: var(--pe-text-primary);
          border-color: var(--pe-border-strong);
          box-shadow: inset 0 1px 3px rgba(28,25,23,0.1);
      }
      html:not(.dark) .editable-field[data-placeholder]:empty::before { color: #A8A29E; }
      html:not(.dark) .calculated-field {
          background: var(--pe-bg-tile-header);
          color: var(--pe-accent-text);
          border-color: var(--pe-border-subtle);
          box-shadow: inset 0 1px 3px rgba(28,25,23,0.08);
      }
      html:not(.dark) input[type="checkbox"] {
          background-color: var(--pe-bg-input);
          border-color: var(--pe-border-strong);
          box-shadow: inset 0 0 2px rgba(28,25,23,0.2);
      }
      html:not(.dark) .action-btn {
          background: var(--pe-bg-card);
          color: var(--pe-accent-text);
          border-color: var(--pe-border-strong);
          box-shadow: 0 1px 3px rgba(28,25,23,0.12);
      }
      html:not(.dark) .remove-btn {
          background-color: var(--pe-bg-tile-header);
          color: #78716C;
          border-color: var(--pe-border-subtle);
      }
      html:not(.dark) .custom-select {
          background-color: var(--pe-bg-input);
          color: var(--pe-accent-text);
          border-color: var(--pe-border-strong);
          box-shadow: inset 0 1px 3px rgba(28,25,23,0.1);
      }
      html:not(.dark) .carbon-weave-bg {
          background-color: var(--pe-bg-elevated);
          background-image: none;
      }
      html:not(.dark) .carbon-panel {
          border-color: var(--pe-border-subtle);
          box-shadow: 0 20px 45px -20px rgba(28, 25, 23, 0.18), 0 2px 8px rgba(28, 25, 23, 0.06);
      }
      html:not(.dark) .carbon-panel::after { background: none; }
      html:not(.dark) .gauge-container-3d {
          background: var(--pe-bg-elevated);
          box-shadow: inset 0 1px 4px rgba(28,25,23,0.08), 0 4px 12px rgba(28,25,23,0.08);
          border-color: var(--pe-border-subtle);
      }
      html:not(.dark) .text-verlust { color: #B91C1C !important; text-shadow: none !important; }
      html:not(.dark) .text-kostendeckend { color: #A16207; text-shadow: none !important; }
      html:not(.dark) .text-gewinn { color: #15803D; text-shadow: none !important; }
      html:not(.dark) .text-gut { color: #1D4ED8; text-shadow: none !important; }
      html:not(.dark) .text-exzellent { color: #6D28D9; text-shadow: none !important; }
      html:not(.dark) .compact-table th, html:not(.dark) .compact-table td { border-bottom-color: var(--pe-border-subtle); }
      html:not(.dark) .compact-table th { background-color: var(--pe-bg-tile-header); color: var(--pe-accent-text); border-bottom-color: var(--pe-border-subtle); }
      html:not(.dark) .modal-input {
          background: var(--pe-bg-input);
          border-color: var(--pe-border-strong);
          color: var(--pe-text-primary);
      }
      html:not(.dark) .toggle-container { background-color: var(--pe-bg-input); border-color: var(--pe-border-strong); }
      html:not(.dark) .toggle-pill-inactive { color: #78716C; }
      html:not(.dark) .toggle-pill-inactive:hover { color: var(--pe-text-primary); }

      @media print {
          .no-print-container { display: none !important; height: 0 !important; overflow: hidden !important; }
      }
    `}</style>
);

const EditableField: React.FC<EditableFieldProps> = ({ value, onSave, isNumeric = false, unit, formatOptions = {}, className = '', align = 'center', tooltip }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const isEditingRef = useRef(false);

    const getFormatted = useCallback((val: any) => {
        return (val != null && isNumeric) ? formatNumberDE(Number(val), formatOptions) : val || '';
    }, [isNumeric, formatOptions]);

    useEffect(() => {
        if (!isEditingRef.current && inputRef.current) {
            inputRef.current.value = String(getFormatted(value));
        }
    }, [value, getFormatted]);

    const handleBlur = () => {
        isEditingRef.current = false;
        if (!inputRef.current) return;
        const rawValue = inputRef.current.value;
        const numValue = parseNumberDE(rawValue);
        
        if (rawValue === '') {
            onSave(0);
        } else if (isNumeric) {
            if (!isNaN(numValue)) onSave(numValue);
            else inputRef.current.value = String(getFormatted(value));
        } else {
            if (rawValue !== value) onSave(rawValue);
        }
        inputRef.current.value = String(isNumeric ? formatNumberDE(parseNumberDE(inputRef.current.value), formatOptions) : inputRef.current.value);
    };

    const isCurrencyUnit = unit && unit !== '%' && unit !== 'St' && unit !== 'min';

    return (
        <div 
            className={`relative flex items-center gap-1 bg-pe-input dark:bg-[#000] border border-pe-border-strong rounded-lg w-full min-h-[44px] px-2.5 py-2 shadow-inner group hover:border-[#E6C299] transition-colors focus-within:border-[#E6C299] focus-within:ring-1 focus-within:ring-[#E6C299] ${tooltip ? 'cursor-help' : 'cursor-text'} ${className}`}
            data-tooltip-id={tooltip ? 'app-tooltip' : undefined}
            data-tooltip-content={tooltip}
        >
            <input
                ref={inputRef}
                type="text"
                defaultValue={getFormatted(value)}
                onFocus={(e) => { isEditingRef.current = true; e.target.select(); }}
                onBlur={handleBlur}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                className={`text-pe-text font-bold font-mono bg-transparent focus:outline-none placeholder:text-stone-400 dark:placeholder:text-gray-600 p-0 m-0 border-0 flex-1 min-w-0 ${align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center'}`}
            />
            {unit && (
                <span className={`flex-shrink-0 text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest pointer-events-none ${isCurrencyUnit ? 'text-[9px]' : 'text-[10px]'}`}>
                    {unit}
                </span>
            )}
        </div>
    );
};

const MarginCalculator: React.FC<{ mwstRate: number }> = ({ mwstRate }) => {
    const settings = useAppStore(state => state.settings);
    const currency = settings?.country?.currency === 'CHF' ? 'CHF' : 'EUR';

    const [vatMode, setVatMode] = React.useState('bn');
    const [calcMode, setCalcMode] = React.useState('target_vk');
    const [vatValue, setVatValue] = React.useState(36.50);
    const [ekValue, setEkValue] = React.useState(80.00);
    const [margeValue, setMargeValue] = React.useState(100.00);
    const [vkValue, setVkValue] = React.useState(160.00);

    const mwstFaktor = mwstRate / 100;
    const calculatedVatResult = vatMode === 'bn' ? vatValue / (1 + mwstFaktor) : vatValue * (1 + mwstFaktor);
    const calculatedVk = ekValue * (1 + margeValue / 100);
    const calculatedMarge = ekValue > 0 ? ((vkValue / ekValue) - 1) * 100 : 0;

    return (
        <div className="carbon-panel shadow-2xl h-full flex flex-col extensions-kalkulator-panel bg-pe-card dark:bg-[#030303]">
            <div className="carbon-content flex flex-col h-full">
                <div className="carbon-weave-bg px-6 py-4 border-b border-pe-border dark:border-[#222]">
                    <h2 className="text-sm font-bold text-pe-accent-text text-center uppercase tracking-widest">Kalkulator Tools</h2>
                </div>
                <div className="p-5 space-y-6 flex-grow text-pe-text">
                    <div className="cursor-help">
                        <div className="flex justify-center mb-4">
                            <div
                                className="toggle-container shadow-lg"
                                data-tooltip-id="app-tooltip"
                                data-tooltip-content="Zwischen Brutto- und Netto-Eingabe umschalten."
                            >
                                <div className={`toggle-pill ${vatMode === 'bn' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`} onClick={() => setVatMode('bn')}>Brutto</div>
                                <div className="toggle-arrow">⇄</div>
                                <div className={`toggle-pill ${vatMode === 'nb' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`} onClick={() => setVatMode('nb')}>Netto</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 items-end bg-pe-tile-header p-4 rounded-xl border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
                            <div>
                                <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">{vatMode === 'bn' ? 'Eingabe (Brutto)' : 'Eingabe (Netto)'}</div>
                                <EditableField
                                    value={vatValue}
                                    onSave={setVatValue}
                                    isNumeric
                                    unit={currency}
                                    formatOptions={{ minimumFractionDigits: 2 }}
                                    tooltip={`Betrag eingeben – umgerechnet wird mit ${formatNumberDE(mwstRate, { maximumFractionDigits: 1 })} % MwSt.`}
                                />
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">{vatMode === 'bn' ? 'Ergebnis (Netto)' : 'Ergebnis (Brutto)'}</div>
                                <div
                                    className="bg-pe-card border border-pe-border dark:border-[#222] rounded-lg px-3 py-2 text-center text-pe-accent-text font-bold font-mono shadow-inner h-[48px] flex items-center justify-center extensions-margin-calc-result cursor-help"
                                    data-tooltip-id="app-tooltip"
                                    data-tooltip-content="Umgerechneter Gegenwert inklusive bzw. exklusive Mehrwertsteuer."
                                >
                                    {formatCurrencyValue(calculatedVatResult)} <span className="text-[0.7rem] ml-1 text-stone-500 dark:text-gray-500 font-sans">{currency}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <hr className="border-[#222]" />
                    
                    <div className="cursor-help">
                        <div className="flex justify-center mb-4">
                            <div
                                className="toggle-container shadow-lg"
                                data-tooltip-id="app-tooltip"
                                data-tooltip-content="Zwischen VK-Preis-Berechnung und Marge-Prüfung umschalten."
                            >
                                <div className={`toggle-pill ${calcMode === 'target_vk' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`} onClick={() => setCalcMode('target_vk')}>VK-Preis</div>
                                <div className="toggle-arrow">⇄</div>
                                <div className={`toggle-pill ${calcMode === 'target_marge' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`} onClick={() => setCalcMode('target_marge')}>Marge</div>
                            </div>
                        </div>
                        {calcMode === 'target_vk' ? (
                            <div className="space-y-4 bg-pe-tile-header p-4 rounded-xl border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">EK Preis</div>
                                        <EditableField
                                            value={ekValue}
                                            onSave={setEkValue}
                                            isNumeric
                                            unit={currency}
                                            formatOptions={{ minimumFractionDigits: 2 }}
                                            tooltip="Netto-Einkaufspreis des Artikels oder der Packung."
                                        />
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">Marge %</div>
                                        <EditableField
                                            value={margeValue}
                                            onSave={setMargeValue}
                                            isNumeric
                                            unit="%"
                                            tooltip="Aufschlag in Prozent auf den Einkaufspreis (z. B. 100 % = Verdopplung)."
                                        />
                                    </div>
                                </div>
                                <div className="pt-2 border-t border-[#1a1a1a]">
                                    <div className="text-pe-accent-text text-[10px] uppercase tracking-widest font-bold text-center pb-2">Verkaufspreis (Brutto)</div>
                                    <div
                                        className="bg-pe-card border border-customBeige/30 rounded-lg p-3 text-center text-pe-accent-text text-xl font-bold font-mono shadow-[0_0_15px_rgba(230,194,153,0.1)] h-[50px] flex items-center justify-center extensions-margin-calc-result cursor-help"
                                        data-tooltip-id="app-tooltip"
                                        data-tooltip-content="Berechneter Verkaufspreis aus EK und Marge (ohne separate MwSt-Umrechnung in diesem Hilfsrechner)."
                                    >
                                        {formatCurrencyValue(calculatedVk)} <span className="text-[0.8rem] ml-1.5 text-stone-500 dark:text-gray-500 font-sans">{currency}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4 bg-pe-tile-header p-4 rounded-xl border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">EK Preis</div>
                                        <EditableField
                                            value={ekValue}
                                            onSave={setEkValue}
                                            isNumeric
                                            unit={currency}
                                            formatOptions={{ minimumFractionDigits: 2 }}
                                            tooltip="Netto-Einkaufspreis des Artikels oder der Packung."
                                        />
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-pe-muted uppercase tracking-wider text-center pb-2">VK Preis</div>
                                        <EditableField
                                            value={vkValue}
                                            onSave={setVkValue}
                                            isNumeric
                                            unit={currency}
                                            formatOptions={{ minimumFractionDigits: 2 }}
                                            tooltip="Verkaufspreis brutto zum Prüfen der erreichten Marge."
                                        />
                                    </div>
                                </div>
                                <div className="pt-2 border-t border-[#1a1a1a]">
                                    <div className="text-pe-accent-text text-[10px] uppercase tracking-widest font-bold text-center pb-2">Erzielte Marge</div>
                                    <div
                                        className="bg-pe-card border border-customBeige/30 rounded-lg p-3 text-center text-pe-accent-text text-xl font-bold font-mono shadow-[0_0_15px_rgba(230,194,153,0.1)] h-[50px] flex items-center justify-center extensions-margin-calc-result cursor-help"
                                        data-tooltip-id="app-tooltip"
                                        data-tooltip-content="Errechnete prozentuale Marge aus EK und VK-Preis."
                                    >
                                        {formatNumberDE(calculatedMarge, { maximumFractionDigits: 1 })} <span className="text-[0.8rem] ml-1 text-stone-500 dark:text-gray-500 font-sans">%</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AuswertungResult: React.FC<AuswertungResultProps> = ({ label, value, subtext, valueClassName = '', variant = 'default', additionalInfo, tooltip }) => {
    const { val, unit } = splitValueAndUnit(value);
    return (
        <div
            className={`flex flex-col gap-2 text-center p-6 rounded-xl h-full justify-start shadow-inner transition-all duration-300 ${variant === 'highlight' ? 'bg-pe-sunken border border-customBeige/40 shadow-[0_0_20px_rgba(230,194,153,0.1)]' : 'bg-pe-tile-header border border-pe-border dark:border-[#222]'} ${tooltip ? 'cursor-help' : ''}`}
            data-tooltip-id={tooltip ? 'app-tooltip' : undefined}
            data-tooltip-content={tooltip}
        >
            <label className="text-xs font-semibold text-pe-muted uppercase tracking-wider">{label}</label>
            <div className="flex flex-col items-center mt-2">
                <div className={`${variant === 'highlight' ? 'text-4xl' : 'text-3xl'} ${valueClassName} font-mono font-bold whitespace-nowrap`}>
                    {val}<span className="text-base lg:text-lg text-gray-600 ml-2 font-sans font-normal uppercase tracking-widest">{unit}</span>
                </div>
                {subtext && <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider mt-1">{subtext}</span>}
            </div>
            {additionalInfo && <div className="text-[10px] text-gray-600 mt-3 pt-3 border-t border-[#1a1a1a] uppercase tracking-widest">{additionalInfo}</div>}
        </div>
    );
};

// --- HAUPTKOMPONENTE ---
const ExtensionskalkulatorPage: React.FC = () => {
    const { clientData, setClientData, settings } = useAppStore();
    
    const currency = settings?.country?.currency === 'CHF' ? 'CHF' : 'EUR';

    // Daten aus dem Store beziehen
    const kalkState = clientData.extensionsKalkulator;
    const inputs = kalkState.inputs;
    const extensions = kalkState.extensions;
    const selectedExtensionId = kalkState.selectedExtensionId;

    // lineItems: Multi-Select mit Menge pro Variante (persistiert in extensionsKalkulator; Legacy-Fallback)
    const lineItems = useMemo(() => {
        if (Array.isArray(kalkState.lineItems)) {
            return kalkState.lineItems.filter((li: any) => li && li.extensionId);
        }
        if (selectedExtensionId) {
            return [{ extensionId: selectedExtensionId, menge: inputs.menge > 0 ? inputs.menge : 1 }];
        }
        return [];
    }, [kalkState.lineItems, selectedExtensionId, inputs.menge]);

    const gesamtMenge = lineItems.reduce((sum: number, li: any) => sum + (Number(li.menge) || 0), 0);

    const storeCalc = useCalculationsSelector();
    const currentMwstRate =
        storeCalc?.mwstSatz !== undefined && storeCalc?.mwstSatz !== null
            ? Number(storeCalc.mwstSatz)
            : clientData.isVatExempt
                ? 0
                : (settings.country?.MwSt_Satz ?? 19);
    const mwstFaktor = currentMwstRate / 100;

    // --- STORE ACTIONS ---
    const updateKalkState = useCallback((newPartial: any) => {
        setClientData((prev: any) => ({
            ...prev,
            extensionsKalkulator: { ...prev.extensionsKalkulator, ...newPartial }
        }));
    }, [setClientData]);

    const handleInputChange = (field: keyof ExtensionsKalkulatorInputs, value: number) => {
        updateKalkState({ inputs: { ...inputs, [field]: value } });
    };

    const commitLineItems = useCallback((nextItems: Array<{ extensionId: string; menge: number }>) => {
        const sum = nextItems.reduce((s, li) => s + (Number(li.menge) || 0), 0);
        updateKalkState({
            lineItems: nextItems,
            selectedExtensionId: nextItems.length > 0 ? nextItems[0].extensionId : null,
            inputs: { ...inputs, menge: sum }
        });
    }, [updateKalkState, inputs]);

    const toggleLineItem = (extensionId: string, checked: boolean) => {
        if (checked) {
            if (lineItems.some((li: any) => li.extensionId === extensionId)) return;
            commitLineItems([...lineItems, { extensionId, menge: 1 }]);
        } else {
            commitLineItems(lineItems.filter((li: any) => li.extensionId !== extensionId));
        }
    };

    const updateLineItemMenge = (extensionId: string, mengeRaw: number) => {
        const menge = !isNaN(mengeRaw) && mengeRaw > 0 ? mengeRaw : 1;
        commitLineItems(
            lineItems.map((li: any) =>
                li.extensionId === extensionId ? { ...li, menge } : li
            )
        );
    };

    const handleExtensionChange = (id: string, field: keyof ExtensionsKalkulatorExtension, value: any) => {
        const newExtensions = extensions.map(ext => ext.id === id ? { ...ext, [field]: value } : ext);
        updateKalkState({ extensions: newExtensions });
    };

    const addExtension = () => {
        const newId = `custom_${Date.now()}`;
        const newExtension: ExtensionsKalkulatorExtension = { 
            id: newId, 
            name: 'Neuer Artikel', 
            packContent: '1 St.', 
            piecesPerPack: 1, 
            ekNetto: 0, 
            verkaufsMarge: 100 
        };
        const nextItems = [...lineItems, { extensionId: newId, menge: 1 }];
        const sum = nextItems.reduce((s, li) => s + (Number(li.menge) || 0), 0);
        updateKalkState({ 
            extensions: [...extensions, newExtension],
            lineItems: nextItems,
            selectedExtensionId: newId,
            inputs: { ...inputs, menge: sum }
        });
    };

    const deleteExtension = (idToDelete: string) => {
        const remaining = extensions.filter(ext => ext.id !== idToDelete);
        const nextItems = lineItems.filter((li: any) => li.extensionId !== idToDelete);
        const sum = nextItems.reduce((s: number, li: any) => s + (Number(li.menge) || 0), 0);
        updateKalkState({ 
            extensions: remaining,
            lineItems: nextItems,
            selectedExtensionId: nextItems.length > 0 ? nextItems[0].extensionId : null,
            inputs: { ...inputs, menge: sum }
        });
    };

    const selectedExtensions = lineItems
        .map((li: any) => extensions.find(e => e.id === li.extensionId))
        .filter(Boolean) as ExtensionsKalkulatorExtension[];

    // Wie Simplie: Name + Menge je Variante (z. B. „PM Flaunt … (2x)“)
    const headerAuswahlLabel = lineItems.length === 0
        ? 'Kein Produkt gewählt'
        : lineItems.map((li: any) => {
            const ext = extensions.find(e => e.id === li.extensionId);
            const name = ext?.name || li.extensionId;
            const menge = Number(li.menge) > 0 ? Number(li.menge) : 1;
            return `${name} (${menge}x)`;
        }).join(' · ');

    const primaryExtension = selectedExtensions[0] || { 
        name: 'Unbekannt', 
        ekNetto: 0, 
        piecesPerPack: 1, 
        verkaufsMarge: 0 
    } as ExtensionsKalkulatorExtension;
    
    // --- RECHENLOGIK (MULTI-ITEM: Summe über lineItems) ---
    let materialkostenNetto = 0;
    let vkHaareBrutto = 0;
    for (const li of lineItems) {
        const ext = extensions.find(e => e.id === li.extensionId);
        if (!ext) continue;
        const stk = ext.piecesPerPack || 1;
        const ekStk = (ext.ekNetto || 0) / stk;
        const mat = ekStk * (Number(li.menge) || 0);
        materialkostenNetto += mat;
        vkHaareBrutto += mat * (1 + (ext.verkaufsMarge || 0) / 100);
    }
    const ekProStueck = gesamtMenge > 0 ? materialkostenNetto / gesamtMenge : 0;
    const stkProPaket = primaryExtension.piecesPerPack || 1;
    
    const dynamicWaWiFaktor = settings.global?.Warenwirtschaftsaufschlag_DL || 1.05;
    const warenwirtschaftskostenNetto = materialkostenNetto * (dynamicWaWiFaktor - 1);
    const warenwirtschaftskostenBrutto = warenwirtschaftskostenNetto * (1 + mwstFaktor);

    const vkHaareNetto = vkHaareBrutto / (1 + mwstFaktor);
    const gewinnHaareNetto = vkHaareNetto - materialkostenNetto - warenwirtschaftskostenNetto;
    const angezeigteGewinnMargeProzent = materialkostenNetto > 0 ? (gewinnHaareNetto / materialkostenNetto) * 100 : 0;

    const arbeitskostenNetto = (inputs.einarbeitungszeit / 60) * inputs.stundensatz;
    const einarbeitungskostenBrutto = arbeitskostenNetto * (1 + mwstFaktor);
    const gesamtpreisBrutto = einarbeitungskostenBrutto + vkHaareBrutto;
    
    const dynamicStundenSatz = settings.global?.Stundensatz || 75.00;
    const dynamicProfitMarge = (settings.global?.Liquiditaet || 10.6) / 100;
    const gewinnProStunde = dynamicStundenSatz * dynamicProfitMarge; 
    const gewinnProHerrenhaarschnitt = (dynamicStundenSatz / 2) * dynamicProfitMarge; 

    const anzahlHerren = gewinnHaareNetto > 0 ? gewinnHaareNetto / gewinnProHerrenhaarschnitt : 0; 
    const anzahlStunden = gewinnHaareNetto > 0 ? gewinnHaareNetto / gewinnProStunde : 0; 

    return (
        <div className="min-h-screen bg-pe-bg dark:bg-[#030303] p-4 sm:p-8 font-sans text-pe-muted dark:text-slate-300">
            <CustomStyles />
            <main className="mx-auto max-w-[1300px] carbon-panel mb-20 extensions-kalkulator-panel bg-pe-card dark:bg-[#030303]">
                <div className="carbon-content">
                    {/* LOGO-BANNER */}
                    <div className="bg-white border-b border-gray-200 p-6 shadow-sm">
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-center sm:text-left max-w-4xl mx-auto">
                            <div className="flex-shrink-0 border-2 border-slate-300 rounded-2xl p-4 shadow-md bg-white flex items-center justify-center">
                                <img src="https://i.imgur.com/jcihp5G.png" alt="Simplie Logo" className="h-14 w-auto" />
                            </div>
                            <div className="flex-grow">
                                <p className="font-black text-gray-900 text-xl tracking-wide">Simplie Kalkulator gesucht?</p>
                                <p className="text-xs text-gray-600 font-bold uppercase tracking-widest mt-1">Speziell für Simplie Partner optimiert.</p>
                                <p className="text-[11px] text-stone-500 dark:text-gray-500 mt-3 max-w-lg leading-relaxed font-medium">
                                    Simplie folgt einer eigenen System-Logik. Nutzen Sie bitte ausschließlich den offiziellen Rechner, um korrekte Ergebnisse nach den System-Vorgaben zu erhalten.
                                </p>
                            </div>
                            <div className="flex-shrink-0 mt-4 sm:mt-0">
                                {/* 💉 ANBINDUNG AN DIE NEUE ISOLIERTE ROUTE */}
                                <a 
                                    href={typeof window !== 'undefined' ? window.location.origin + '/simplie-kalkulator' : '/simplie-kalkulator'} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const url = window.location.origin + '/simplie-kalkulator';
                                        const win = window.open(url, '_blank');
                                        if (!win) {
                                            window.location.assign(url);
                                        }
                                    }}
                                    className="bg-black hover:bg-gray-800 text-white font-black py-3 px-8 rounded-xl transition-all shadow-md text-xs uppercase tracking-widest inline-flex items-center gap-3"
                                >
                                    Zum Simplie Tool <ExternalLink size={16} />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Header */}
                    <div className="p-6 sm:p-10 text-center border-b border-[#1a1a1a] carbon-weave-bg">
                        <h1 className="text-3xl font-black text-pe-text mb-6 uppercase tracking-widest drop-shadow-lg">Extensionskalkulator</h1>
                        <div className="mb-10 inline-block border border-customBeige/30 rounded-xl bg-white/60 dark:bg-[#000]/60 px-8 py-3 text-center shadow-[inset_0_2px_15px_rgba(230,194,153,0.05)] max-w-full">
                            <span className="block text-xs font-semibold text-pe-muted uppercase tracking-wider mb-1">Aktuelle Auswahl</span>
                            <span className="text-xl text-pe-accent-text font-black uppercase tracking-wide drop-shadow-md break-words">{headerAuswahlLabel}</span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-end max-w-5xl mx-auto">
                            <div className="flex flex-col gap-2 rounded-xl border border-pe-border dark:border-[#2a2a2a] p-3 bg-pe-card dark:bg-[#030303]">
                                <label className="text-xs font-semibold text-pe-muted uppercase tracking-wider">Gesamtmenge Haare (automatisch)</label>
                                <div
                                    className="relative flex items-center justify-center gap-1 bg-pe-tile-header dark:bg-[#000]/40 border border-pe-border-strong rounded-lg w-full min-h-[44px] px-2.5 py-2 shadow-inner cursor-help"
                                    data-tooltip-id="app-tooltip"
                                    data-tooltip-content="Summe aller Mengen der aktivierten Haarvarianten in der Tabelle."
                                >
                                    <span className="text-pe-accent-text font-bold font-mono text-lg">{formatNumberDE(gesamtMenge)}</span>
                                    <span className="flex-shrink-0 text-stone-500 dark:text-gray-500 font-bold uppercase tracking-widest text-[10px]">Stk</span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2 rounded-xl border border-pe-border dark:border-[#2a2a2a] p-3 bg-pe-card dark:bg-[#030303]">
                                <label className="text-xs font-semibold text-pe-muted uppercase tracking-wider">Stundensatz</label>
                                <div className="relative group cursor-help" data-tooltip-id="app-tooltip" data-tooltip-content="Wählen Sie hier den Stundensatz. Wir empfehlen mindestens 120 € Stundensatz.">
                                    <select 
                                        value={inputs.stundensatz} 
                                        onChange={(e) => handleInputChange('stundensatz', parseFloat(e.target.value))}
                                        className="w-full appearance-none rounded-lg bg-pe-input dark:bg-[#000] border border-pe-border-strong p-3 text-center font-mono font-bold text-pe-text focus:outline-none focus:border-customBeige focus:ring-1 focus:ring-customBeige h-[52px] cursor-pointer shadow-inner transition-colors"
                                    >
                                        {Array.from({ length: 27 }, (_, i) => 70 + i * 5).map(opt => (
                                            <option key={opt} value={opt} className="bg-pe-card">{opt} {currency}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="absolute right-4 top-4 text-stone-500 dark:text-gray-500 group-hover:text-customBeige pointer-events-none transition-colors" />
                                </div>
                            </div>
                            <div className="flex flex-col gap-2 rounded-xl border border-pe-border dark:border-[#2a2a2a] p-3 bg-pe-card dark:bg-[#030303]">
                                <label className="text-xs font-semibold text-pe-muted uppercase tracking-wider">Einarbeitungszeit</label>
                                <EditableField 
                                    value={inputs.einarbeitungszeit} 
                                    onSave={(val: any) => handleInputChange('einarbeitungszeit', val)} 
                                    isNumeric 
                                    unit="min" 
                                    className="!py-3 !text-lg"
                                    tooltip="Die reine Zeit für das Einsetzen der Menge (ohne Schnitt/Farbe)." 
                                />
                            </div>
                            <div
                                className="text-center border border-pe-border dark:border-[#2a2a2a] rounded-xl p-2 shadow-inner h-[52px] flex flex-col justify-center cursor-help bg-pe-card dark:bg-[#030303]"
                                data-tooltip-id="app-tooltip"
                                data-tooltip-content="Prozentualer Gewinn auf den Materialwert (Reingewinn ÷ Materialkosten netto)."
                            >
                                <label className="text-[9px] font-bold uppercase tracking-widest text-gray-600 mb-0.5">Gewinn-Marge</label>
                                <div className="text-xl font-black font-mono text-pe-accent-text drop-shadow-md">{formatNumberDE(Math.round(angezeigteGewinnMargeProzent))}%</div>
                            </div>
                        </div>
                    </div>

                    {/* Ergebnisse */}
                    <div className="p-6 sm:p-10 bg-pe-input dark:bg-[#000]/40 grid grid-cols-1 md:grid-cols-3 gap-8">
                        <AuswertungResult 
                            label="Material-Basis" 
                            value={formatCurrencyStrict(ekProStueck, currency)} 
                            subtext="pro Stück (netto)" 
                            additionalInfo={
                                lineItems.length > 1
                                    ? `(Mischkalkulation · ${formatNumberDE(gesamtMenge)} Stk)`
                                    : (stkProPaket > 1 ? `(Paketpreis ${formatCurrencyStrict(primaryExtension.ekNetto, currency)} ÷ ${stkProPaket} Stk)` : '(Einzelartikel)')
                            }
                            tooltip="Netto-Materialkosten pro eingesetztem Einzelstück (Einkaufspreis ÷ Stück pro Packung)."
                        />
                        <AuswertungResult 
                            label="VK-Preis Haare" 
                            value={formatCurrencyStrict(vkHaareBrutto, currency)} 
                            subtext={`brutto (${currentMwstRate}%)`} 
                            valueClassName="text-pe-accent-text" 
                            additionalInfo={`(inkl. ${formatCurrencyStrict(warenwirtschaftskostenBrutto, currency)} WaWi)`}
                            tooltip="Brutto-Verkaufspreis der Extensions inkl. Aufschlag und Warenwirtschaft."
                        />
                        <AuswertungResult 
                            label="Gesamtpreis" 
                            value={formatCurrencyStrict(gesamtpreisBrutto, currency)} 
                            subtext="Rechnungsbetrag" 
                            valueClassName="text-pe-text" 
                            variant="highlight"
                            tooltip="Gesamtbetrag für den Kunden: Extensions plus Einarbeitungszeit."
                        />
                    </div>

                    {/* Gewinn & Äquivalenz */}
                    <div className="p-6 sm:p-10 border-t border-[#1a1a1a]">
                        <div
                            className="p-8 rounded-2xl bg-pe-sunken border border-pe-border dark:border-[#222] mb-10 shadow-[inset_0_2px_15px_rgba(0,0,0,0.8)] cursor-help"
                            data-tooltip-id="app-tooltip"
                            data-tooltip-content="Netto-Gewinn aus dem Verkauf der Extensions nach Abzug von Material und Warenwirtschaft."
                        >
                            <div className="flex flex-col md:flex-row justify-between items-center text-lg gap-6">
                                <span className="text-pe-accent-text uppercase tracking-widest font-black text-sm text-center md:text-left">Gewinn<br/><span className="text-xs font-semibold text-pe-muted">(Verkauf Haare)</span></span>
                                <div className="flex items-baseline gap-2 border-b border-pe-border-strong pb-2 px-6">
                                    <span className="text-pe-accent-text font-mono font-black text-4xl drop-shadow-lg">{splitValueAndUnit(formatCurrencyStrict(gewinnHaareNetto, currency)).val}</span>
                                    <span className="text-stone-500 dark:text-gray-500 font-sans font-bold text-lg uppercase tracking-widest">{currency}</span>
                                </div>
                            </div>
                            <div
                                className="mt-8 pt-6 border-t border-[#1a1a1a] text-center cursor-help"
                                data-tooltip-id="app-tooltip"
                                data-tooltip-content="Vergleich: Wie viele Dienstleistungen bzw. Arbeitsstunden entsprechen diesem Materialgewinn?"
                            >
                                <p className="text-gray-600 text-[10px] mb-3 uppercase tracking-widest font-black">Äquivalenz-Check</p>
                                <p className="text-pe-muted dark:text-gray-300 text-sm leading-loose">
                                    Für diesen Gewinn müsstest du <span className="font-bold text-pe-text dark:text-white bg-pe-tile-header dark:bg-black px-3 py-1 rounded border border-pe-border-strong font-mono mx-1">{formatNumberDE(anzahlHerren, {maximumFractionDigits: 0})}</span> Herrenhaarschnitte machen oder <span className="font-bold text-pe-text dark:text-white bg-pe-tile-header dark:bg-black px-3 py-1 rounded border border-pe-border-strong font-mono mx-1">{formatNumberDE(anzahlStunden, {minimumFractionDigits: 1, maximumFractionDigits: 1})}</span> Std. arbeiten.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-10 text-pe-text">
                            <div className="lg:col-span-2 xl:col-span-3 border border-pe-border dark:border-[#222] rounded-2xl overflow-hidden flex flex-col bg-pe-card dark:bg-[#030303] shadow-2xl extensions-kalkulator-config">
                                <div className="carbon-weave-bg px-6 py-4 border-b border-pe-border dark:border-[#222]">
                                    <h2 className="text-xs font-black text-customBeige text-center uppercase tracking-widest">Extensions Konfiguration</h2>
                                </div>
                                <div className="overflow-x-auto p-2 extensions-kalkulator-table-scroll">
                                    <table className="w-full text-left text-sm table-fixed">
                                        <colgroup>
                                            <col style={{ width: '7.5rem' }} />
                                            <col />
                                            <col style={{ width: '5rem' }} />
                                            <col style={{ width: '10.5rem' }} />
                                            <col style={{ width: '6.5rem' }} />
                                            <col style={{ width: '3rem' }} />
                                        </colgroup>
                                        <thead className="text-xs font-semibold text-pe-muted uppercase tracking-wider border-b border-pe-border dark:border-[#222]">
                                            <tr>
                                                <th className="p-4 text-center">Wahl</th>
                                                <th className="p-4 min-w-[260px]" data-tooltip-id="app-tooltip" data-tooltip-content="Tragen Sie hier den Namen der Extension ein (z. B. Tape, Tresse, Bondings).">Produkt</th>
                                                <th className="p-4 w-20 text-center">Stk/Pkt</th>
                                                <th className="p-4 w-[10.5rem]">EK (netto)</th>
                                                <th className="p-4 w-[6.5rem]">Marge %</th>
                                                <th className="p-4 w-12 text-center">X</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[#1a1a1a]">
                                            {extensions.map((ext) => {
                                                const activeItem = lineItems.find((li: any) => li.extensionId === ext.id);
                                                const isActive = !!activeItem;
                                                return (
                                                <tr key={ext.id} className={`transition-colors ${isActive ? 'bg-white/5' : 'hover:bg-white/5'}`}>
                                                    <td className="p-3 text-center">
                                                        <div className="flex flex-col items-center gap-1.5">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isActive} 
                                                                onChange={(e) => toggleLineItem(ext.id, e.target.checked)} 
                                                                className="cursor-pointer" 
                                                            />
                                                            {isActive ? (
                                                                <div className="flex items-center gap-0.5">
                                                                    <button
                                                                        type="button"
                                                                        aria-label="Menge verringern"
                                                                        onClick={() => updateLineItemMenge(ext.id, (Number(activeItem.menge) || 1) - 1)}
                                                                        className="w-6 h-8 flex items-center justify-center bg-slate-900 border border-slate-700 rounded text-slate-100 font-bold text-sm hover:border-[#E6C299] hover:text-[#E6C299] transition-colors"
                                                                    >
                                                                        −
                                                                    </button>
                                                                    <input
                                                                        type="text"
                                                                        inputMode="numeric"
                                                                        value={activeItem.menge}
                                                                        onChange={(e) => {
                                                                            const raw = e.target.value.replace(/[^\d]/g, '');
                                                                            if (raw === '') return;
                                                                            updateLineItemMenge(ext.id, parseInt(raw, 10));
                                                                        }}
                                                                        className="w-10 h-8 text-center bg-slate-900 border border-slate-700 rounded text-slate-100 font-semibold"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        aria-label="Menge erhöhen"
                                                                        onClick={() => updateLineItemMenge(ext.id, (Number(activeItem.menge) || 0) + 1)}
                                                                        className="w-6 h-8 flex items-center justify-center bg-slate-900 border border-slate-700 rounded text-slate-100 font-bold text-sm hover:border-[#E6C299] hover:text-[#E6C299] transition-colors"
                                                                    >
                                                                        +
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-0.5 opacity-40 pointer-events-none">
                                                                    <span className="w-6 h-8 flex items-center justify-center bg-slate-900 border border-slate-700 rounded text-slate-100 font-bold text-sm">−</span>
                                                                    <span className="w-10 h-8 flex items-center justify-center bg-slate-900 border border-slate-700 rounded text-slate-500 font-semibold">–</span>
                                                                    <span className="w-6 h-8 flex items-center justify-center bg-slate-900 border border-slate-700 rounded text-slate-100 font-bold text-sm">+</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        <EditableField
                                                            value={ext.name}
                                                            onSave={(val: any) => handleExtensionChange(ext.id, 'name', val)}
                                                            align="left"
                                                            className="!bg-pe-input dark:!bg-black/50"
                                                            tooltip="Name der Extension eintragen (z. B. Tape 40 cm, Tresse, Bondings)."
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex flex-col items-center">
                                                            <EditableField 
                                                                value={ext.piecesPerPack} 
                                                                onSave={(val: any) => handleExtensionChange(ext.id, 'piecesPerPack', val)} 
                                                                isNumeric 
                                                                unit="St" 
                                                                className="!bg-pe-input dark:!bg-black/50"
                                                                tooltip="Anzahl Teile pro Packung (z. B. 12 bei Tapes, 1 bei Tressen)." 
                                                            />
                                                            {(!ext.piecesPerPack || ext.piecesPerPack < 1) && (
                                                                <span title="Bitte Stückzahl eingeben!">
                                                                    <AlertCircle size={14} className="text-red-500 mt-1" />
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        <EditableField 
                                                            value={ext.ekNetto} 
                                                            onSave={(val: any) => handleExtensionChange(ext.id, 'ekNetto', val)} 
                                                            isNumeric 
                                                            unit={currency} 
                                                            className="!bg-pe-input dark:!bg-black/50"
                                                            formatOptions={{minimumFractionDigits: 2}} 
                                                            tooltip="Netto-Einkaufspreis des kompletten Pakets."
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <EditableField 
                                                            value={ext.verkaufsMarge} 
                                                            onSave={(val: any) => handleExtensionChange(ext.id, 'verkaufsMarge', val)} 
                                                            isNumeric 
                                                            unit="%" 
                                                            className="!bg-pe-input dark:!bg-black/50"
                                                            tooltip="Ihr Aufschlag auf den Materialwert (wir empfehlen 100%+)."
                                                        />
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <button onClick={() => deleteExtension(ext.id)} className="text-gray-600 hover:text-red-500 p-2 transition-colors">
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );})}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="p-4 border-t border-pe-border dark:border-[#222] bg-pe-sunken">
                                    <button onClick={addExtension} className="w-full bg-pe-card hover:bg-white hover:text-black border border-pe-border-strong text-customBeige font-black py-3 rounded-lg text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-all shadow-[0_0_10px_rgba(0,0,0,0.5)]">
                                        <Plus size={16} /> Produkt anlegen
                                    </button>
                                </div>
                            </div>
                            <div className="lg:col-span-1 xl:col-span-1">
                                <MarginCalculator mwstRate={currentMwstRate} />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ExtensionskalkulatorPage;